export interface StandardEventPropertyItem {
  name: string;
  type: string;
}

interface StandardEventMap {
  [key: string]: {
    name: string;
    required: StandardEventPropertyItem[];
    optional: StandardEventPropertyItem[];
  };
}

export const STANDARD_EVENT_PROPERTY_KEYS: { [key: string]: StandardEventPropertyItem } = {
  CONTENT_ID: {
    name: 'content_id',
    type: 'string',
  },
  CONTENT_NAME: {
    name: 'content_name',
    type: 'string',
  },
  CONTENT_CATEGORY: {
    name: 'content_category',
    type: 'string',
  },
  VALUE: {
    name: 'value',
    type: 'double',
  },
  CURRENCY: {
    name: 'currency',
    type: 'string',
  },
  NUM_ITEMS: {
    name: 'num_items',
    type: 'int',
  },
  SEARCH_STRING: {
    name: 'search_string',
    type: 'string',
  },
  SERVICE_NAME: {
    name: 'service_name',
    type: 'string',
  },
  STATUS: {
    name: 'status',
    type: 'string',
  },
  EXTRAS: {
    name: 'extras',
    type: 'object',
  },
  INTERNAL_EVENT_GID: {
    name: 'internal_event_gid',
    type: 'string',
  },
  LINE_PROVIDED_USER_ID: {
    name: 'line_provided_user_id',
    type: 'string',
  },
  LINE_PROVIDED_CHANNEL_ID: {
    name: 'line_provided_channel_id',
    type: 'string',
  },
};

export const STANDARD_EVENTS: StandardEventMap = {
  VIEW_CONTENT: {
    name: 'view_content',
    required: [STANDARD_EVENT_PROPERTY_KEYS.CONTENT_ID],
    optional: [
      STANDARD_EVENT_PROPERTY_KEYS.CONTENT_NAME,
      STANDARD_EVENT_PROPERTY_KEYS.CONTENT_CATEGORY,
      STANDARD_EVENT_PROPERTY_KEYS.VALUE,
      STANDARD_EVENT_PROPERTY_KEYS.CURRENCY,
      STANDARD_EVENT_PROPERTY_KEYS.EXTRAS,
    ],
  },
  SEARCH: {
    name: 'search',
    required: [],
    optional: [
      STANDARD_EVENT_PROPERTY_KEYS.CONTENT_ID,
      STANDARD_EVENT_PROPERTY_KEYS.CONTENT_CATEGORY,
      STANDARD_EVENT_PROPERTY_KEYS.VALUE,
      STANDARD_EVENT_PROPERTY_KEYS.CURRENCY,
      STANDARD_EVENT_PROPERTY_KEYS.SEARCH_STRING,
      STANDARD_EVENT_PROPERTY_KEYS.EXTRAS,
    ],
  },
  ADD_TO_CART: {
    name: 'add_to_cart',
    required: [STANDARD_EVENT_PROPERTY_KEYS.CONTENT_ID],
    optional: [
      STANDARD_EVENT_PROPERTY_KEYS.CONTENT_NAME,
      STANDARD_EVENT_PROPERTY_KEYS.CONTENT_CATEGORY,
      STANDARD_EVENT_PROPERTY_KEYS.VALUE,
      STANDARD_EVENT_PROPERTY_KEYS.CURRENCY,
      STANDARD_EVENT_PROPERTY_KEYS.EXTRAS,
    ],
  },
  ADD_TO_WISHLIST: {
    name: 'add_to_wishlist',
    required: [],
    optional: [
      STANDARD_EVENT_PROPERTY_KEYS.CONTENT_ID,
      STANDARD_EVENT_PROPERTY_KEYS.CONTENT_NAME,
      STANDARD_EVENT_PROPERTY_KEYS.CONTENT_CATEGORY,
      STANDARD_EVENT_PROPERTY_KEYS.VALUE,
      STANDARD_EVENT_PROPERTY_KEYS.CURRENCY,
      STANDARD_EVENT_PROPERTY_KEYS.EXTRAS,
    ],
  },
  INITIATE_CHECKOUT: {
    name: 'initiate_checkout',
    required: [],
    optional: [
      STANDARD_EVENT_PROPERTY_KEYS.CONTENT_ID,
      STANDARD_EVENT_PROPERTY_KEYS.CONTENT_NAME,
      STANDARD_EVENT_PROPERTY_KEYS.CONTENT_CATEGORY,
      STANDARD_EVENT_PROPERTY_KEYS.VALUE,
      STANDARD_EVENT_PROPERTY_KEYS.CURRENCY,
      STANDARD_EVENT_PROPERTY_KEYS.NUM_ITEMS,
      STANDARD_EVENT_PROPERTY_KEYS.EXTRAS,
    ],
  },
  ADD_PAYMENT_INFO: {
    name: 'add_payment_info',
    required: [],
    optional: [
      STANDARD_EVENT_PROPERTY_KEYS.CONTENT_ID,
      STANDARD_EVENT_PROPERTY_KEYS.CONTENT_CATEGORY,
      STANDARD_EVENT_PROPERTY_KEYS.VALUE,
      STANDARD_EVENT_PROPERTY_KEYS.CURRENCY,
      STANDARD_EVENT_PROPERTY_KEYS.EXTRAS,
    ],
  },
  PURCHASE: {
    name: 'purchase',
    required: [
      STANDARD_EVENT_PROPERTY_KEYS.CONTENT_ID,
      STANDARD_EVENT_PROPERTY_KEYS.VALUE,
      STANDARD_EVENT_PROPERTY_KEYS.CURRENCY,
    ],
    optional: [
      STANDARD_EVENT_PROPERTY_KEYS.CONTENT_NAME,
      STANDARD_EVENT_PROPERTY_KEYS.CONTENT_CATEGORY,
      STANDARD_EVENT_PROPERTY_KEYS.NUM_ITEMS,
      STANDARD_EVENT_PROPERTY_KEYS.EXTRAS,
    ],
  },
  SHARE: {
    name: 'share',
    required: [],
    optional: [
      STANDARD_EVENT_PROPERTY_KEYS.CONTENT_ID,
      STANDARD_EVENT_PROPERTY_KEYS.CONTENT_NAME,
      STANDARD_EVENT_PROPERTY_KEYS.CONTENT_CATEGORY,
      STANDARD_EVENT_PROPERTY_KEYS.SERVICE_NAME,
      STANDARD_EVENT_PROPERTY_KEYS.EXTRAS,
    ],
  },
  LEAD: {
    name: 'lead',
    required: [],
    optional: [
      STANDARD_EVENT_PROPERTY_KEYS.CONTENT_NAME,
      STANDARD_EVENT_PROPERTY_KEYS.CONTENT_CATEGORY,
      STANDARD_EVENT_PROPERTY_KEYS.VALUE,
      STANDARD_EVENT_PROPERTY_KEYS.CURRENCY,
      STANDARD_EVENT_PROPERTY_KEYS.EXTRAS,
    ],
  },
  COMPLETE_REGISTRATION: {
    name: 'complete_registration',
    required: [],
    optional: [
      STANDARD_EVENT_PROPERTY_KEYS.CONTENT_NAME,
      STANDARD_EVENT_PROPERTY_KEYS.VALUE,
      STANDARD_EVENT_PROPERTY_KEYS.CURRENCY,
      STANDARD_EVENT_PROPERTY_KEYS.STATUS,
      STANDARD_EVENT_PROPERTY_KEYS.EXTRAS,
    ],
  },
  SHOW_WEB_PUSH_NOTIFICATION: {
    name: 'show_web_push_notification',
    required: [STANDARD_EVENT_PROPERTY_KEYS.INTERNAL_EVENT_GID],
    optional: [],
  },
  CLICK_WEB_PUSH_NOTIFICATION: {
    name: 'click_web_push_notification',
    required: [STANDARD_EVENT_PROPERTY_KEYS.INTERNAL_EVENT_GID],
    optional: [],
  },
  FETCH_WEB_PUSH_NOTIFICATION_DATA: {
    name: 'fetch_web_push_notification_data',
    required: [STANDARD_EVENT_PROPERTY_KEYS.INTERNAL_EVENT_GID],
    optional: [],
  },
  LINK_LINE_ID: {
    name: 'link_line_id',
    required: [
      STANDARD_EVENT_PROPERTY_KEYS.LINE_PROVIDED_USER_ID,
      STANDARD_EVENT_PROPERTY_KEYS.LINE_PROVIDED_CHANNEL_ID,
    ],
    optional: [STANDARD_EVENT_PROPERTY_KEYS.EXTRAS],
  },
  UNLINK_LINE_ID: {
    name: 'unlink_line_id',
    required: [
      STANDARD_EVENT_PROPERTY_KEYS.LINE_PROVIDED_USER_ID,
      STANDARD_EVENT_PROPERTY_KEYS.LINE_PROVIDED_CHANNEL_ID,
    ],
    optional: [STANDARD_EVENT_PROPERTY_KEYS.EXTRAS],
  },
};
